import { Popover, TextField } from "@mui/material";
import { LocalizationProvider, YearPicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { formatDate } from "utils/formatDate";
import {
  generateUrlWithParams,
  paramParsify,
  paramStringify,
} from "utils/queryParams";

const InputYearPickerComponent = ({ value, limitToFiveYears = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const today = dayjs();

  const parsedQueryParams = useMemo(
    () => paramParsify(location?.search),
    [location],
  );

  const [date, setDate] = useState(
    dayjs(parsedQueryParams?.year) || dayjs(Date.now()),
  );
  const [anchorEl, setAnchorEl] = useState(null);
  const [category, setCategory] = useState(
    parsedQueryParams?.year ? "year" : "all",
  );

  const handleClickTextField = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, []);

  const handleClickByYear = useCallback(() => {
    setCategory("year");

    if (!parsedQueryParams?.year) {
      setDate(dayjs(Date.now()));
      const stringifiedParams = paramStringify({
        ...parsedQueryParams,
        year: formatDate.toShortYear(Date.now()),
      });
      navigate(generateUrlWithParams(location?.pathname, stringifiedParams), {
        replace: true,
      });
    }
  }, [location?.pathname, navigate, parsedQueryParams]);

  const handleOnChangeYearPicker = useCallback(
    (val) => {
      setDate(val);
      const stringifiedParams = paramStringify({
        ...parsedQueryParams,
        year: formatDate.toShortYear(val),
      });
      navigate(generateUrlWithParams(location?.pathname, stringifiedParams), {
        replace: true,
      });
      handleClose();
    },
    [location?.pathname, navigate, parsedQueryParams, handleClose],
  );

  const handleClickByAll = useCallback(() => {
    setCategory("all");
    const stringifiedParams = paramStringify({
      ...parsedQueryParams,
      year: undefined,
    });
    navigate(generateUrlWithParams(location?.pathname, stringifiedParams), {
      replace: true,
    });
    handleClose();
  }, [location?.pathname, navigate, parsedQueryParams, handleClose]);

  return (
    <>
      <TextField
        onClick={handleClickTextField}
        sx={{ input: { cursor: "pointer" } }}
        InputProps={{
          startAdornment: (
            <img
              className='cursor-pointer w-8 h-8 pl-3'
              src='/images/calendar.svg'
              alt='Calendar'
            />
          ),
          readOnly: true,
        }}
        value={value}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 6,
            },
          },
        }}
      >
        <div className='flex flex-row h-[348px]'>
          <div className='border-r w-[298px] py-4'>
            <div className='flex flex-col'>
              <span
                className={`cursor-pointer px-4 py-2 mb-2 text-[14px] font-semibold text-center ${
                  category === "year" ? "bg-[#EEF1F8]" : ""
                }`}
                onClick={handleClickByYear}
              >
                By year
              </span>
              <span
                className={`cursor-pointer px-4 py-2 text-[14px] font-semibold text-center ${
                  category === "all" ? "bg-[#EEF1F8]" : ""
                }`}
                onClick={handleClickByAll}
              >
                All time
              </span>
            </div>
          </div>
          <div className='border-r w-[338px] py-4'>
            {category === "all" ? (
              <div className='mt-[45px]'>
                <span className='flex flex-1 px-4 py-2 text-[14px] bg-[#EEF1F8] w-full'>{`Until ${formatDate.toShortYear(
                  Date.now(),
                )}`}</span>
              </div>
            ) : (
              <div>
                <LocalizationProvider
                  dateAdapter={AdapterDayjs}
                  adapterLocale='en-gb'
                >
                  <YearPicker
                    onChange={handleOnChangeYearPicker}
                    date={date}
                    maxDate={today}
                    minDate={limitToFiveYears ? dayjs().subtract(5, 'year') : undefined}
                  />
                </LocalizationProvider>
              </div>
            )}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default InputYearPickerComponent;
